import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { showToast, subscribeOnlineStatus, useOnlineStatus } from '@beatq/shared';
import { drainFloorWriteQueue } from './floorWriteSync.js';
import { pendingFloorWriteCount } from './floorWriteQueue.js';

export function useFloorWriteQueue(): { queuedCount: number; drain: () => Promise<void> } {
  const online = useOnlineStatus();
  const qc = useQueryClient();
  const [queuedCount, setQueuedCount] = useState(() => pendingFloorWriteCount());

  const refresh = useCallback(() => {
    setQueuedCount(pendingFloorWriteCount());
  }, []);

  const drain = useCallback(async () => {
    const result = await drainFloorWriteQueue();
    refresh();
    if (result.synced > 0) {
      void qc.invalidateQueries({ queryKey: ['merchant', 'kitchen'] });
      void qc.invalidateQueries({ queryKey: ['merchant', 'orders'] });
      showToast('success', `Synced ${result.synced} queued floor action${result.synced === 1 ? '' : 's'}`);
    }
    if (result.failed > 0) {
      showToast('error', `${result.failed} queued action${result.failed === 1 ? '' : 's'} failed to sync`);
    }
  }, [qc, refresh]);

  useEffect(() => {
    refresh();
    return subscribeOnlineStatus((next, prev) => {
      refresh();
      if (next && !prev && pendingFloorWriteCount() > 0) {
        void drain();
      }
    });
  }, [drain, refresh]);

  useEffect(() => {
    if (online && queuedCount > 0) {
      void drain();
    }
  }, [online, queuedCount, drain]);

  return { queuedCount, drain };
}
